import { useMemo, useRef, useEffect, useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useScoreboard } from '@/hooks/useScoreboard'
import { usePolling } from '@/hooks/usePolling'
import { ScoreCard } from '@/components/ScoreCard'
import { Skeleton } from '@/components/ui/Skeleton'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { formatDayHeader, formatDayChip, recentScoreboardDays } from '@/lib/dateUtils'
import type { ScoreboardDay, ScoreboardGame } from '@/types/api'

const DATE_CHIP_COUNT = 8

function isLiveStatus(status?: string) {
  const s = status?.toLowerCase()
  return s === 'live' || s === 'in_progress'
}

// Homepage is the scoreboard: it leads with a strip of the biggest recent
// results, then the day feed (today, or last night in the morning) with
// infinite-scroll backward and a quick-jump date strip. The week-filterable
// view lives at /:sport/scores.
export default function Dashboard() {
  const { sport, sportLabel } = useSport()
  const {
    days,
    featured,
    loadingInitial,
    loadingMore,
    initialError,
    moreError,
    hasMore,
    loadMore,
    loadUntil,
    retryInitial,
    refetchAnchorDay,
  } = useScoreboard(sport)

  // Live-poll only the anchor (first) section, and only while it has a live game.
  const anchorHasLive = useMemo(
    () => (days[0]?.games ?? []).some((g) => isLiveStatus(g.status)),
    [days],
  )
  usePolling(refetchAnchorDay, 30_000, anchorHasLive)

  // Infinite scroll. Refs keep the observer reading the latest loadMore + gating
  // state without re-binding on every render.
  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore
  const canLoadMoreRef = useRef(false)
  canLoadMoreRef.current = hasMore && !moreError && !loadingMore && !loadingInitial

  // Observe the sentinel via a callback ref so the observer reconnects if the
  // node remounts (e.g. after an initial-error retry) instead of watching a
  // stale, detached node.
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const attachSentinel = useCallback((node: HTMLDivElement | null) => {
    sentinelNodeRef.current = node
    observerRef.current?.disconnect()
    if (!node) return
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canLoadMoreRef.current) loadMoreRef.current()
      },
      { rootMargin: '600px' }, // prefetch the next day before the user hits the end
    )
    observerRef.current.observe(node)
  }, [])

  // Auto-fill: a short day (or an empty anchor) can leave the sentinel on-screen
  // with no scroll to re-trigger the observer, stalling the feed. Keep loading
  // until the sentinel is pushed below the fold or history runs out.
  useEffect(() => {
    if (!canLoadMoreRef.current) return
    const el = sentinelNodeRef.current
    if (!el) return
    if (el.getBoundingClientRect().top <= window.innerHeight + 600) loadMore()
  }, [days, hasMore, loadingMore, moreError, loadingInitial, loadMore])

  // Scrollspy: highlight the date chip for the day currently under the strip.
  const [activeDate, setActiveDate] = useState<string | null>(null)
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-day-date]'))
    if (nodes.length === 0) return
    const visible = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const date = (entry.target as HTMLElement).dataset.dayDate
          if (!date) continue
          if (entry.isIntersecting) visible.add(date)
          else visible.delete(date)
        }
        // Dates descend down the page, so the topmost visible day is the max date.
        if (visible.size > 0) {
          const sorted = [...visible].sort()
          setActiveDate(sorted[sorted.length - 1] ?? null)
        }
      },
      { rootMargin: '-110px 0px -70% 0px' },
    )
    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [days])

  // Quick-jump: load back far enough, then scroll to the target day (or the
  // nearest loaded older day when the target itself has no games).
  const [pendingJump, setPendingJump] = useState<string | null>(null)
  const handleJump = useCallback((date: string) => {
    setPendingJump(date)
    loadUntil(date)
  }, [loadUntil])

  useEffect(() => {
    if (!pendingJump) return
    const loaded = days.map((d) => d.date).filter((d): d is string => !!d)
    const oldest = loaded[loaded.length - 1]
    const reached = (oldest !== undefined && oldest <= pendingJump) || !hasMore || moreError
    if (!reached) return // still paging toward the target

    const older = loaded.filter((d) => d <= pendingJump).sort()
    const nearest = older[older.length - 1] ?? loaded[0]
    const el =
      document.getElementById(`day-${pendingJump}`) ??
      (nearest ? document.getElementById(`day-${nearest}`) : null)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setPendingJump(null)
  }, [pendingJump, days, hasMore, moreError])

  const loadedDates = useMemo(
    () => new Set(days.map((d) => d.date).filter(Boolean) as string[]),
    [days],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{sportLabel} Scores</h1>
          <p className="text-sm text-slate-400">Biggest results up top &mdash; scroll for previous days</p>
        </div>
        <Link
          to={`/${sport}/scores`}
          className="shrink-0 text-sm font-medium text-accent hover:text-accent-light transition-colors"
        >
          Filter &amp; seasons &rarr;
        </Link>
      </div>

      {!loadingInitial && !initialError && featured.length > 0 && (
        <FeaturedStrip games={featured} sport={sport} />
      )}

      {initialError ? (
        <FeedError onRetry={retryInitial} full />
      ) : loadingInitial ? (
        <DaySkeleton />
      ) : (
        <>
          <DateStrip
            dates={recentScoreboardDays(DATE_CHIP_COUNT)}
            activeDate={activeDate}
            loadedDates={loadedDates}
            onJump={handleJump}
          />
          {days.map((day) => (
            <DaySection key={day.date ?? 'anchor'} day={day} sport={sport} />
          ))}
        </>
      )}

      {/* Sentinel + bottom states (hidden entirely on the initial error screen). */}
      {!initialError && (
        <div ref={attachSentinel} className="min-h-4 pt-2">
          {loadingMore && <DaySkeleton compact />}
          {moreError && !loadingMore && <FeedError onRetry={loadMore} />}
          {!hasMore && !loadingMore && days.some((d) => d.games.length > 0) && (
            <p className="py-6 text-center text-sm text-slate-600">
              You&rsquo;ve reached the end of recent scores.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function FeaturedStrip({ games, sport }: { games: ScoreboardGame[]; sport: string }) {
  return (
    <section>
      <SectionHeader title="Biggest Recent Scores" linkTo={`/${sport}/scores`} />
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
        {games.map((game) => (
          <div key={game.id} className="snap-start shrink-0 w-72">
            <ScoreCard game={game} sport={sport} />
          </div>
        ))}
      </div>
    </section>
  )
}

function DateStrip({
  dates,
  activeDate,
  loadedDates,
  onJump,
}: {
  dates: string[]
  activeDate: string | null
  loadedDates: Set<string>
  onJump: (date: string) => void
}) {
  // Sticky just under the app header (h-14); day headers stick below this strip.
  return (
    <div className="sticky top-14 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 py-2 bg-pitch-950/95 backdrop-blur-md border-b border-pitch-700/50">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {dates.map((date) => {
          const active = date === activeDate
          return (
            <button
              key={date}
              onClick={() => onJump(date)}
              aria-current={active ? 'true' : undefined}
              className={[
                'shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                active
                  ? 'bg-accent text-white'
                  : loadedDates.has(date)
                    ? 'bg-pitch-800 text-slate-200 hover:bg-pitch-700'
                    : 'bg-pitch-900 text-slate-400 hover:bg-pitch-800',
              ].join(' ')}
            >
              {formatDayChip(date)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function DaySection({ day, sport }: { day: ScoreboardDay; sport: string }) {
  const label = day.date ? formatDayHeader(day.date) : 'Today'
  const count = day.games.length

  return (
    // scroll-mt clears the sticky app header + date strip when jumped to.
    <section id={day.date ? `day-${day.date}` : undefined} data-day-date={day.date ?? undefined} className="scroll-mt-28">
      {/* Sticky below the date strip (app header h-14 + strip ≈ 6.5rem). */}
      <div className="sticky top-[6.5rem] z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 py-2 bg-pitch-950/95 backdrop-blur-md">
        <div className="flex items-baseline gap-3">
          <h2 className="text-lg font-bold text-slate-100">{label}</h2>
          {count > 0 && (
            <span className="text-xs text-slate-500">
              {count} {count === 1 ? 'game' : 'games'}
            </span>
          )}
        </div>
      </div>

      {count === 0 ? (
        <div className="card p-6 mt-3 text-center">
          <p className="text-sm text-slate-500">
            {label === 'Today' ? 'No games scheduled today yet.' : 'No games this day.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
          {day.games.map((game) => (
            <ScoreCard key={game.id} game={game} sport={sport} />
          ))}
        </div>
      )}
    </section>
  )
}

function DaySkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? '' : 'space-y-3'}>
      {!compact && <div className="skeleton h-6 w-32 rounded" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: compact ? 3 : 6 }).map((_, i) => (
          <Skeleton key={i} variant="card" className="h-44" />
        ))}
      </div>
    </div>
  )
}

function FeedError({ onRetry, full = false }: { onRetry: () => void; full?: boolean }) {
  return (
    <div className={`card text-center ${full ? 'p-10' : 'p-6'}`}>
      <p className="text-sm text-slate-400">
        {full ? "Couldn't load scores." : "Couldn't load older scores."}
      </p>
      <button
        onClick={onRetry}
        className="mt-3 px-4 py-1.5 rounded-lg text-sm bg-pitch-800 text-slate-200 hover:bg-pitch-700 transition-colors"
      >
        Retry
      </button>
    </div>
  )
}
